<template>
    <div class="box call-to-action">
        <h2 class="box-title">
            <base-checkbox
                :container-class="['settings-option']"
                label=""
                value="cta"
                :values="hasCtaButton"
                @change="updateCtaButton"
            />
            Call to Action
        </h2>
        <div
            class="box-content"
        >
            <form
                id="call-to-action"
                novalidate
                @submit.prevent="saveInfo"
            >
                <p class="description">
                    <template v-if="hasCtaButton">
                        Choose your default call to action to display on all of your players (you can override these settings in individual player options):
                    </template>
                    <template v-else>
                        Check above if you want to add a default call to action to your podcast player.
                    </template>
                </p>
                <base-settings-row
                    v-if="hasCtaButton"
                    class="is-centered"
                    config-title="Action"
                    input-type="select"
                    :fields-config="ctaConfig"
                    :binded-value="config.target"
                    settings-key="target"
                    :is-active="hasCtaButtonFeature"
                    @change="value => controlsChange(value)"
                />
                <base-settings-row
                    v-if="hasCtaButton"
                    config-title="Button"
                    class="button-text"
                    input-type="text"
                    :binded-value="config.label"
                    :fields-config="ctaButtonTextLabel"
                    settings-key="label"
                    :is-active="hasCtaButtonFeature"
                    @change="value => controlsChange(value)"
                />
                <base-settings-row
                    v-if="hasCtaButton"
                    class="url-field"
                    config-title="URL"
                    input-type="text"
                    :binded-value="config.url"
                    :fields-config="ctaUrlConfig"
                    settings-key="url"
                    :is-active="hasCtaButtonFeature"
                    @change="value => controlsChange(value)"
                />
                <div class="controll-container flex">
                    <base-form-button
                        class="btn"
                        :loading="formProcessing"
                    >
                        Save Changes
                    </base-form-button>
                </div>
            </form>
        </div>
    </div>
</template>

<script>
import playerSettings from '@/utils/config/playerSettings';
import { Feature, hasFeature } from '@/utils/show';
import toasts from '@/mixins/toasts';
import { isEqual } from 'lodash';

const { ctaButton } = playerSettings;

export default {
    mixins: [toasts],

    props: {
        show: {
            type: Object,
            default: () => ({}),
        },

        formProcessing: {
            type: Boolean,
            required: true,
        },
    },

    data: () => ({
        ...ctaButton,

        config: {
            buttons: [],
            label: '',
            target: '',
            url: '',
        },
    }),

    computed: {
        /**
         * Check if CTA button is present in buttons config array
         *
         * @returns  {boolean}  If CTA button is present.
         */
        hasCtaButton() {
            return this.config.buttons.includes('cta');
        },

        /**
         * Checks if the show has CTA Button feature enabled.
         *
         * @returns {boolean} True if the show has CTA Button feature.
         */
        hasCtaButtonFeature() {
            return hasFeature(this.show, Feature.CTAButton);
        },
    },

    watch: {
        show: {
            immediate: true,

            /**
             * Manage data.
             *
             * @param {object} show Show object data.
             * @param {*} oldShow Show object data.
             */
            handler(show, oldShow) {
                if (isEqual(show, oldShow)) {
                    return;
                }

                const { buttonConfig: { cta } } = show;

                this.config = {
                    buttons: show.buttons,
                    ...cta,
                };
            },
        },
    },

    methods: {
        /**
         * Update CTA button value.
         *
         * @param  {boolean} value Indicates whether CTA checkbox is checked or not.
         */
        updateCtaButton(value) {
            if (value) {
                this.config.buttons.push('cta');
            } else {
                this.config.buttons.splice(this.config.buttons.indexOf('cta'), 1);
            }
        },

        /**
         * Sets component data based on configuration object
         *
         * @param   {object}  config  Object with configuration
         *
         */
        controlsChange(config) {
            for (const key in config) {
                this.config[key] = config[key];
            }
        },

        /**
         * Submit form
         *
         * @returns {void} Do nothing, now.
         */
        saveInfo() {
            if (! this.validate()) {
                return;
            }

            const { buttons, label, target, url } = this.config;

            this.$emit('submit', {
                buttons,
                buttonConfig: {
                    cta: { label, target, url },
                },
            });
        },

        /**
         * Validates user input
         *
         * @returns  {boolean}  If all fields are entered
         */
        validate() {
            if (! this.hasCtaButton) {
                return true;
            }

            const { label, url } = this.config;
            const hasLabel = this.validateField(label, 'Please enter Button Label');
            const hasUrl = this.validateField(url, 'Please enter Button URL');

            return hasLabel && hasUrl;
        },

        /**
         * Validates field and displays error
         *
         * @param   {string}  value    Field value
         * @param   {string}  message  Error message
         *
         * @returns  {boolean}           If field is valid
         */
        validateField(value, message) {
            ! value && this.toastError({
                message: {
                    title: message,
                },
            });

            return !! value;
        },
    },
};
</script>
